import React, { useState, useEffect, useCallback } from 'react';
import { GoogleMapProvider } from '@ubilabs/google-maps-react-hooks';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import SuperClusterAlgorithm from '../Utils/superClusterAlgorithm';
import { useMapTaxiBusinessData } from '../hooks/useMapTaxiBusinessData';
import { MapTaxiBusinessDrivers, MapTaxiBusinessRiders } from '../../../shared/types';
import { getDriverColor, getLastSeenText, getRiderIcon, getTaxiIcon } from '../../../shared/Utils';
import { useRef } from 'react';

const MapContainer = () => {
    const {
        mapData: { riders, drivers, isClusteringMode },
        setMapData: setMapData,
    } = useMapTaxiBusinessData();

    const driverMarkersRef = useRef<Map<string, google.maps.Marker>>(new Map());
    const riderMarkersRef = useRef<google.maps.Marker[]>([]);

    const driverClusterRef = useRef<MarkerClusterer | null>(null);
    const riderClusterRef = useRef<MarkerClusterer | null>(null);

    const driverPathsRef = useRef<
        Map<
            string,
            {
                points: google.maps.LatLngLiteral[];
                polyline: google.maps.Polyline;
                color: string;
            }
        >
    >(new Map());
    const driverClickListenersRef = useRef<Map<string, google.maps.MapsEventListener>>(new Map());

    const [mapContainer, setMapContainer] = useState(null);
    const [map, setMap] = useState(null);
    const [center, setCenter] = useState({ lat: 36.7376, lng: 10.205 });

    useEffect(() => {
        if (!map) return;

        addRidersMarkers(map, riders);
        addTaxiDriversMarkers(map, drivers);
    }, [map, riders, drivers, isClusteringMode]);

    const onLoad = useCallback((map: google.maps.Map) => {
        setMap(map);
    }, []);

    const addTaxiDriversMarkers = (map: google.maps.Map, drivers: MapTaxiBusinessDrivers[]) => {
        // Remove previous cluster
        driverClusterRef.current?.clearMarkers();

        // Remove previous markers from the map
        const infoWindow = new google.maps.InfoWindow();

        const markers = (drivers ?? []).flatMap((driver) => {
            const isOnline = driver?.isOnline ?? false;

            if (!driver.currentLocation?.latitude || !driver.currentLocation?.longitude) return [];
            
            let marker = driverMarkersRef.current.get(`marker_${driver._id}`);
            if (marker) {
                marker.setPosition({
                    lat: driver.currentLocation.latitude,
                    lng: driver.currentLocation.longitude,
                });

                marker.setIcon(getTaxiIcon(driver.isOnline));
            } else {
                marker = new google.maps.Marker({
                    position: {
                        lat: driver.currentLocation.latitude,
                        lng: driver.currentLocation.longitude,
                    },
                    map,
                    icon: getTaxiIcon(driver.isOnline),
                });

                driverMarkersRef.current.set(`marker_${driver._id}`, marker);
            }

            const oldListener = driverClickListenersRef.current.get(`marker_${driver._id}`);
            if (oldListener) {
                oldListener.remove();
            }
            const listener = marker.addListener('click', () => {
                infoWindow.setContent(`
                    <div>
                        <h4>${driver.fullName}</h4>

                        <img
                            src="${driver?.userPhotoUrl || '/assets/user.png'}"
                            width="48"
                            height="48"
                            style="border-radius:50%;object-fit:cover;margin-right:10px;"
                        />

                        <p>
                            Status:
                            <span style="color:${isOnline ? 'green' : 'red'}">
                                ${isOnline ? '🟢 Online' : '🔴 Offline'}
                            </span>
                        </p>

                        <p>
                            Last seen:
                            ${getLastSeenText(Number(driver?.updatedDate))}
                        </p>
                    </div>
                `);

                infoWindow.open(map, marker);
            });

            driverClickListenersRef.current.set(`marker_${driver._id}`, listener);
            let path = driverPathsRef.current.get(`path_${driver._id}`);
            const position = {
                lat: driver.currentLocation.latitude,
                lng: driver.currentLocation.longitude,
            };

            if (!path) {
                path = {
                    points: [position],
                    color: getDriverColor(driver._id),
                    polyline: new google.maps.Polyline({
                        map,
                        strokeColor: getDriverColor(driver._id),
                        strokeWeight: 2,
                        icons: [
                            {
                                icon: {
                                    path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                                    scale: 2,
                                },
                                offset: "100%",
                            },
                        ],
                    })
                };

                driverPathsRef.current.set(`path_${driver._id}`, path);
            } else {
                const last = path.points[path.points.length - 1];

                // Only add if moved
                if (
                    last.lat !== position.lat ||
                    last.lng !== position.lng
                ) {
                    path.points.push(position);

                    path.polyline.setPath(path.points);
                }
            }

            return [marker];
        });

        if (isClusteringMode) {
            driverClusterRef.current = new MarkerClusterer({
                map,
                markers,
                algorithm: new SuperClusterAlgorithm({ radius: 100 }),
            });
        } else {
            markers.forEach((marker) => marker.setMap(map));
        }
    };

    const addRidersMarkers = (map: google.maps.Map, riders: MapTaxiBusinessRiders[]) => {
        // Remove previous cluster
        riderClusterRef.current?.clearMarkers();

        // Remove previous markers from the map
        riderMarkersRef.current.forEach((marker) => marker.setMap(null));
        riderMarkersRef.current = [];

        const infoWindow = new google.maps.InfoWindow();

        const markers = (riders ?? []).flatMap((rider) => {
            const isOnline = rider?.isOnline ?? false;

            if (!rider.currentLocation?.latitude || !rider.currentLocation?.longitude) return [];

            const marker = new google.maps.Marker({
                position: {
                    lat: rider.currentLocation?.latitude,
                    lng: rider.currentLocation?.longitude,
                },
                icon: getRiderIcon(isOnline),
                map,
            });

            marker.addListener('click', () => {
                infoWindow.setContent(`
                    <div>
                        <h4>${rider.fullName}</h4>
                        <img
                            src="${rider?.userPhotoUrl || '/assets/user.png'}"
                            width="48"
                            height="48"
                            style="border-radius:50%;object-fit:cover;margin-right:10px;"
                        />
                        <p>
                            Status:
                            <span style="color:${isOnline ? 'green' : 'red'}">
                                ${isOnline ? '🟢 Online' : '🔴 Offline'}
                            </span>
                        </p>
                        <p>
                            Last seen:
                            ${getLastSeenText(Number(rider?.updatedDate))}
                        </p>
                    </div>
                `);

                infoWindow.open(map, marker);
            });

            return [marker];
        });

        riderMarkersRef.current = markers;

        if (isClusteringMode) {
            riderClusterRef.current = new MarkerClusterer({
                map,
                markers,
                algorithm: new SuperClusterAlgorithm({ radius: 100 }),
            });
        } else {
            markers.forEach((marker) => marker.setMap(map));
        }
    };

    return (
        <div>
            <GoogleMapProvider
                googleMapsAPIKey={`${process.env.REACT_APP_GOOGLE_API_KEY}&libraries=drawing&callback`}
                options={{ zoom: 9, center }}
                mapContainer={mapContainer}
                onLoad={onLoad}
            >
                <div ref={(node) => setMapContainer(node)} style={{ height: '100vh' }} />
            </GoogleMapProvider>
        </div>
    );
};
export default MapContainer;
